import { useId, useState } from 'react';
import { parseAmountToCents, type Cents } from '@equinox/shared';
import { useI18n } from '../i18n/I18nContext.js';

export interface MoneyInputProps {
  label: string;
  /** Current committed value, in centavos. `null` while the field is empty. */
  valueCents: Cents | null;
  onChange(cents: Cents | null, valid: boolean): void;
  disabled?: boolean;
  autoFocus?: boolean;
  /**
   * Overrides the input's accessible name — used where `label` is
   * deliberately blank for a compact layout (a table cell) but the field
   * still needs a real name for assistive tech. When set, this wins over the
   * associated `<label>` per the ARIA accessible-name computation order.
   */
  ariaLabel?: string;
  /**
   * Table-cell variant: the input alone, no caption line above it and no hint
   * or error line below it, so the control occupies exactly one row's worth of
   * height. The centavos preview and the validation message are not dropped —
   * they move to `title` (hover) and an `aria-describedby` span (assistive
   * tech), neither of which takes up any space. Stacking three lines inside a
   * table cell is what tripled the row height in OneDev xyntara/equinox #1.
   */
  compact?: boolean;
}

/**
 * Pesos-with-decimals entry that never touches parseFloat. Every keystroke
 * runs through @equinox/shared's parseAmountToCents, and the resulting
 * centavos value is always shown next to the field so a typo is visible
 * before saving, not after.
 */
export function MoneyInput({
  label,
  valueCents,
  onChange,
  disabled,
  autoFocus,
  ariaLabel,
  compact,
}: MoneyInputProps) {
  const { t } = useI18n();
  const id = useId();
  const describedById = `${id}-desc`;
  const [raw, setRaw] = useState(() => (valueCents == null ? '' : (valueCents / 100).toFixed(2)));
  const [touched, setTouched] = useState(false);

  const cents = raw.trim() === '' ? null : parseAmountToCents(raw);
  const invalid = touched && raw.trim() !== '' && cents === null;

  function handleChange(next: string) {
    setRaw(next);
    setTouched(true);
    const parsed = next.trim() === '' ? null : parseAmountToCents(next);
    onChange(parsed, next.trim() === '' || parsed !== null);
  }

  if (compact) {
    const description = invalid
      ? t('products.priceInvalid')
      : cents == null
        ? ''
        : t('products.priceCentsPreview', { cents });
    return (
      <div className="field field--compact">
        <span className="input-affix input-affix--prefix">
          <span className="input-affix__symbol" aria-hidden="true">
            $
          </span>
          <input
            id={id}
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={raw}
            disabled={disabled}
            autoFocus={autoFocus}
            aria-invalid={invalid}
            aria-label={ariaLabel ?? label}
            aria-describedby={description ? describedById : undefined}
            title={description || undefined}
            onChange={(e) => handleChange(e.target.value)}
          />
        </span>
        {/* Out of flow (position: absolute), so it describes without occupying. */}
        <span id={describedById} className="sr-only">
          {description}
        </span>
      </div>
    );
  }

  return (
    <div className="field">
      {/*
       * The label is its own element, not one wrapping the input: the "$"
       * affix below is real visible text, and if it sat inside a wrapping
       * <label> it would fold straight into the label's accessible name
       * ("Precio$" instead of "Precio"), breaking every
       * `getByLabelText(label)` lookup. `htmlFor`/`id` ties the two together
       * the normal way instead.
       */}
      <label htmlFor={id}>{label}</label>
      <span className="input-affix input-affix--prefix">
        <span className="input-affix__symbol" aria-hidden="true">
          $
        </span>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          placeholder="0.00"
          value={raw}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-invalid={invalid}
          aria-label={ariaLabel}
          onChange={(e) => handleChange(e.target.value)}
        />
      </span>
      {invalid ? (
        <span className="field-error">{t('products.priceInvalid')}</span>
      ) : (
        <span className="field-hint numeric">
          {cents == null ? ' ' : t('products.priceCentsPreview', { cents })}
        </span>
      )}
    </div>
  );
}
